import { z } from 'zod'

/**
 * Messaging: merge fields, SMS segments, keywords, email blocks, and the
 * send-rate arithmetic. Pure, so the composer's preview and the worker's send
 * are the same computation.
 */

// ---- merge fields ------------------------------------------------------------------

/** The fields a template may use, and what each says when the value is missing. */
export const MERGE_FIELDS = {
  firstName: { label: 'First name', fallback: 'friend' },
  lastName: { label: 'Last name', fallback: '' },
  fullName: { label: 'Full name', fallback: 'friend' },
  churchName: { label: 'Church name', fallback: 'our church' },
  email: { label: 'Email', fallback: '' },
} as const
export type MergeField = keyof typeof MERGE_FIELDS

export type MergeContext = Partial<Record<MergeField, string | null | undefined>>

const MERGE = /\{\{\s*([^{}|]*?)\s*(?:\|\s*([^{}]*?)\s*)?\}\}/g

/**
 * Fills `{{firstName}}`-style fields. `{{firstName|there}}` gives an inline
 * fallback; otherwise each field has a default ("friend" for a name).
 *
 * The guarantee is the acceptance criterion: **the output never contains a
 * merge tag**. A missing value, an unknown field, a typo (`{{frist}}`), a
 * stray `{{` with no close — none of them reach a real inbox as braces. A
 * typo renders its fallback, or nothing.
 */
export function renderTemplate(template: string, context: MergeContext): string {
  const filled = template.replace(MERGE, (_, rawName: string, inline: string | undefined) => {
    const name = rawName.trim() as MergeField
    const value = name in MERGE_FIELDS ? context[name]?.trim() : undefined
    if (value) return value
    if (inline !== undefined) return inline
    return name in MERGE_FIELDS ? MERGE_FIELDS[name].fallback : ''
  })
  // Anything still shaped like a tag (an unclosed `{{`, a nested brace) is
  // stripped rather than sent: a broken template must fail safe. Whole runs
  // of two or more braces go at once — removing `}}` alone can join two lone
  // `{` into a fresh `{{` (the property test found that one), but the
  // characters either side of a whole run are never braces.
  return filled.replace(/[{}]{2,}/g, '')
}

/** Fields a template uses that are not known — the composer warns on these. */
export function unknownMergeFields(template: string): string[] {
  const unknown = new Set<string>()
  for (const match of template.matchAll(MERGE)) {
    const name = (match[1] ?? '').trim()
    if (!(name in MERGE_FIELDS)) unknown.add(name)
  }
  return [...unknown]
}

// ---- SMS --------------------------------------------------------------------------

/** The GSM 03.38 basic set (plus the extension table, which costs two septets). */
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const GSM_EXTENDED = '^{}\\[~]|€\f'

export interface SmsSegments {
  encoding: 'GSM-7' | 'UCS-2'
  /** Characters as the network counts them (extended GSM characters count twice). */
  units: number
  segments: number
  /** Left in the current segment. */
  remaining: number
}

/**
 * How many messages a text will be billed as. GSM-7: 160 per message, 153
 * each once it splits. Anything outside GSM-7 (an emoji, a curly quote) turns
 * the whole text UCS-2: 70, then 67. The composer shows this live because one
 * smart quote can double the cost of a broadcast.
 */
export function smsSegments(text: string): SmsSegments {
  let gsm = true
  let septets = 0
  for (const char of text) {
    if (GSM_BASIC.includes(char)) septets += 1
    else if (GSM_EXTENDED.includes(char)) septets += 2
    else {
      gsm = false
      break
    }
  }
  if (gsm) {
    const segments = septets <= 160 ? 1 : Math.ceil(septets / 153)
    const capacity = segments === 1 ? 160 : segments * 153
    return {
      encoding: 'GSM-7',
      units: septets,
      segments: Math.max(1, segments),
      remaining: capacity - septets,
    }
  }
  // UCS-2 counts UTF-16 code units: an emoji outside the BMP is two.
  const units = text.length
  const segments = units <= 70 ? 1 : Math.ceil(units / 67)
  const capacity = segments === 1 ? 70 : segments * 67
  return { encoding: 'UCS-2', units, segments: Math.max(1, segments), remaining: capacity - units }
}

export type SmsKeyword = 'stop' | 'start' | 'help' | null

/**
 * The carrier-mandated keywords (CTIA): a text that *is* one of these words,
 * ignoring case, spacing and punctuation. "Stop the music" is not a STOP.
 */
export function classifySmsKeyword(body: string): SmsKeyword {
  const word = body
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
  if (
    ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'OPTOUT', 'REVOKE'].includes(word)
  )
    return 'stop'
  if (['START', 'UNSTOP', 'YES', 'OPTIN'].includes(word)) return 'start'
  if (['HELP', 'INFO'].includes(word)) return 'help'
  return null
}

// ---- email blocks -----------------------------------------------------------------

export const emailBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heading'), text: z.string().max(200) }),
  z.object({ type: z.literal('paragraph'), text: z.string().max(5000) }),
  z.object({ type: z.literal('button'), text: z.string().max(80), url: z.url() }),
  z.object({ type: z.literal('divider') }),
])
export type EmailBlock = z.infer<typeof emailBlockSchema>

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )

/**
 * Renders the composer's blocks as an email: HTML for clients that show it
 * and the same words as plain text for those that do not (and for spam
 * filters, which distrust HTML-only mail). Merge fields are filled first, and
 * every value is escaped — a member named `<script>` is a name, not markup.
 */
export function renderEmail(
  blocks: EmailBlock[],
  context: MergeContext,
  footer: { churchName: string; unsubscribeUrl: string; preferencesUrl: string },
): { html: string; text: string } {
  const html: string[] = []
  const text: string[] = []
  for (const block of blocks) {
    if (block.type === 'heading') {
      const value = renderTemplate(block.text, context)
      html.push(`<h2 style="font-family:sans-serif;margin:0 0 12px">${escapeHtml(value)}</h2>`)
      text.push(value.toUpperCase(), '')
    } else if (block.type === 'paragraph') {
      const value = renderTemplate(block.text, context)
      html.push(
        `<p style="font-family:sans-serif;line-height:1.5;margin:0 0 12px">${escapeHtml(value).replace(/\n/g, '<br>')}</p>`,
      )
      text.push(value, '')
    } else if (block.type === 'button') {
      const label = renderTemplate(block.text, context)
      html.push(
        `<p style="margin:16px 0"><a href="${escapeHtml(block.url)}" style="background:#2563eb;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none;font-family:sans-serif">${escapeHtml(label)}</a></p>`,
      )
      text.push(`${label}: ${block.url}`, '')
    } else {
      html.push('<hr style="border:none;border-top:1px solid #ddd;margin:16px 0">')
      text.push('---', '')
    }
  }
  html.push(
    `<p style="font-family:sans-serif;font-size:12px;color:#666;margin-top:24px">${escapeHtml(footer.churchName)} · ` +
      `<a href="${escapeHtml(footer.unsubscribeUrl)}">Unsubscribe</a> · <a href="${escapeHtml(footer.preferencesUrl)}">Email preferences</a></p>`,
  )
  text.push(
    `${footer.churchName}`,
    `Unsubscribe: ${footer.unsubscribeUrl}`,
    `Preferences: ${footer.preferencesUrl}`,
  )
  return {
    html: `<div style="max-width:600px;margin:0 auto">${html.join('')}</div>`,
    text: text.join('\n'),
  }
}

// ---- sending rate -----------------------------------------------------------------

/**
 * A token bucket: `ratePerSecond` sends a second on average, bursts of up to
 * `burst`. Pure state + a clock, so the worker uses the wall clock and a test
 * uses a virtual one — and can prove no one-second window ever exceeds the
 * provider's limit across 20,000 sends without waiting for them.
 */
export interface Bucket {
  tokens: number
  updatedAt: number
}

export function takeToken(
  bucket: Bucket,
  now: number,
  ratePerSecond: number,
  burst: number,
): { bucket: Bucket; waitMs: number } {
  const refilled = Math.min(
    burst,
    bucket.tokens + ((now - bucket.updatedAt) / 1000) * ratePerSecond,
  )
  if (refilled >= 1) return { bucket: { tokens: refilled - 1, updatedAt: now }, waitMs: 0 }
  const waitMs = Math.ceil(((1 - refilled) / ratePerSecond) * 1000)
  return { bucket: { tokens: refilled, updatedAt: now }, waitMs }
}

// ---- workflows --------------------------------------------------------------------

export const WORKFLOW_TRIGGERS = [
  'manual',
  'person_tagged',
  'first_gift',
  'birthday',
  'registration_completed',
  'missed_attendance',
] as const
export type WorkflowTrigger = (typeof WORKFLOW_TRIGGERS)[number]

export const TRIGGER_LABELS: Record<WorkflowTrigger, string> = {
  manual: 'Started by hand',
  person_tagged: 'A person is given a tag',
  first_gift: 'Someone gives for the first time',
  birthday: 'It is someone’s birthday',
  registration_completed: 'Someone registers for an event',
  missed_attendance: 'A group member misses several weeks',
}

export const workflowStepSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('send_email'),
    subject: z.string().min(1).max(200),
    body: z.string().min(1).max(10_000),
  }),
  z.object({ type: z.literal('send_sms'), body: z.string().min(1).max(1_000) }),
  z.object({
    type: z.literal('wait'),
    days: z.number().int().min(0).max(365),
    hours: z.number().int().min(0).max(23).default(0),
  }),
  z.object({
    type: z.literal('assign_task'),
    title: z.string().min(1).max(200),
    assigneeUserId: z.uuid(),
    dueInDays: z.number().int().min(0).max(365).default(3),
  }),
  z.object({ type: z.literal('add_tag'), tagId: z.uuid() }),
  z.object({ type: z.literal('add_to_group'), groupId: z.uuid() }),
  z.object({
    type: z.literal('branch'),
    /** Continue only if the person has (or lacks) this tag; otherwise finish. */
    condition: z.object({ hasTagId: z.uuid(), negate: z.boolean().default(false) }),
  }),
])
export type WorkflowStep = z.infer<typeof workflowStepSchema>

export const STEP_LABELS: Record<WorkflowStep['type'], string> = {
  send_email: 'Send an email',
  send_sms: 'Send a text',
  wait: 'Wait',
  assign_task: 'Assign a task',
  add_tag: 'Add a tag',
  add_to_group: 'Add to a group',
  branch: 'Only continue if…',
}

/** When a wait step started at `from` is over. */
export function waitUntil(from: Date, step: { days: number; hours?: number }): Date {
  return new Date(from.getTime() + (step.days * 24 + (step.hours ?? 0)) * 3_600_000)
}
